import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from './AuthContext';
import logger from '../utils/logger';

const TenantContext = createContext();

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export const TenantProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [currentTenant, setCurrentTenant] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load tenants when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      loadTenants();
    } else {
      setCurrentTenant(null);
      setTenants([]);
      setLoading(false);
    }
  // loadTenants is intentionally invoked on auth/user change only (adding it would loop)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);

  // Set default tenant when tenants are loaded
  useEffect(() => {
    if (tenants.length > 0 && !currentTenant) {
      // Try to get tenant from localStorage first
      const savedTenantId = localStorage.getItem('currentTenantId');
      if (savedTenantId) {
        const savedTenant = tenants.find(t => t.id === parseInt(savedTenantId));
        if (savedTenant) {
          setCurrentTenant(savedTenant);
          setTenantHeader(savedTenant.id);
          return;
        }
      }

      // Use default tenant from user or first tenant
      const defaultTenant = tenants.find(t => t.id === user?.default_tenant_id) || tenants[0];
      if (defaultTenant) {
        setCurrentTenant(defaultTenant);
        setTenantHeader(defaultTenant.id);
        localStorage.setItem('currentTenantId', defaultTenant.id.toString());
      }
    }
  }, [tenants, currentTenant, user]);

  // Set X-Tenant-ID header in axios
  const setTenantHeader = (tenantId) => {
    if (tenantId) {
      axios.defaults.headers.common['X-Tenant-ID'] = tenantId.toString();
    } else {
      delete axios.defaults.headers.common['X-Tenant-ID'];
    }
  };

  // Load user's tenants
  const loadTenants = async () => {
    try {
      setLoading(true);
      
      // If user has tenants in login response, use them
      if (user?.tenants && Array.isArray(user.tenants)) {
        setTenants(user.tenants);
        setLoading(false);
        return;
      }

      // Otherwise, fetch from API
      const response = await axios.get('/api/tenants');
      if (response.data.success && response.data.data) {
        setTenants(response.data.data);
      } else {
        // Fallback: create default tenant from user's default_tenant_id
        if (user?.default_tenant_id) {
          setTenants([{
            id: user.default_tenant_id,
            name: 'Default Tenant',
            slug: 'default',
            is_active: true
          }]);
        }
      }
    } catch (error) {
      logger.error('Error loading tenants:', error);
      // Fallback: create default tenant from user's default_tenant_id
      if (user?.default_tenant_id) {
        setTenants([{
          id: user.default_tenant_id,
          name: 'Default Tenant',
          slug: 'default',
          is_active: true
        }]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Switch to a different tenant
  const switchTenant = async (tenantId) => {
    try {
      const tenant = tenants.find(t => t.id === tenantId);
      if (!tenant) {
        toast.error('Tenant не е пронајден');
        return { success: false };
      }

      setCurrentTenant(tenant);
      setTenantHeader(tenant.id);
      localStorage.setItem('currentTenantId', tenant.id.toString());

      // Reload page to refresh all data with new tenant
      window.location.reload();

      toast.success(`Префрлено на ${tenant.name}`);
      return { success: true };
    } catch (error) {
      logger.error('Error switching tenant:', error);
      toast.error('Грешка при префрлање на tenant');
      return { success: false };
    }
  };

  // Refresh tenants list
  const refreshTenants = async () => {
    await loadTenants();
  };

  const value = {
    currentTenant,
    tenants,
    loading,
    switchTenant,
    refreshTenants,
    hasMultipleTenants: tenants.length > 1,
    canSwitchTenant: tenants.length > 1
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

